import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../../components/ui/card";
import {
  FileText,
  Download,
  Loader2,
  X,
  AlertCircle,
  Upload,
  Eye,
  Camera as CameraIcon,
  Image,
  Trash2,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useIntl, FormattedMessage } from "react-intl";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  GetAttachmentsAPI,
  UploadFilesAPI,
  DeleteFileAPI,
} from "../Files/filesAPI";
import { clearFilesError } from "../Files/FilesSlice";
import { saveAs } from "file-saver";
function FilesTab({ appointmentId }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const filesState = useSelector((state) => state.files) || {};
  const {
    files = [],
    loading = false,
    error = null,
    uploadLoading = false,
    deleteLoading = false,
  } = filesState;

  useEffect(() => {
    if (appointmentId) {
      dispatch(GetAttachmentsAPI(appointmentId));
    }
  }, [dispatch, appointmentId]);

  const handleFileUpload = async (selectedFiles) => {
    if (!appointmentId || selectedFiles.length === 0) {
      return;
    }

    try {
      const uploadPromises = selectedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(",")[1];
            resolve({ fileName: file.name, fileBase64: base64 });
          };
          reader.onerror = () => reject(new Error("Failed to read file"));
          reader.readAsDataURL(file);
        });
      });

      const filesForUpload = await Promise.all(uploadPromises);

      const payload = {
        formId: appointmentId,
        formTypeId: 9,
        files: filesForUpload,
      };

      dispatch(UploadFilesAPI(payload, appointmentId));
    } catch (error) {
      console.error("Failed to process files:", error);
    }
  };

  const handleCameraCapture = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFileUpload(Array.from(e.target.files));
      }
    };
    input.click();
  };

  const handleGalleryUpload = () => {
    document.getElementById("file-input")?.click();
  };

  const removeFile = (fileId) => {
    if (fileId && !deleteLoading) {
      dispatch(DeleteFileAPI([fileId]));
    }
  };

  const dismissError = () => {
    dispatch(clearFilesError());
  };

  const getFilePreview = (file) => {
    const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(
      file.fileType
    );
    if (isImage && file.base64Content) {
      return `data:image/${file.fileType};base64,${file.base64Content}`;
    }
    return null;
  };

  // Just replace the downloadFile function in your existing FilesTab component:

  const downloadFile = (file) => {
    if (!file.base64Content) {
      alert("File content not available");
      return;
    }

    try {
      const mimeType = getMimeType(file.fileType);
      const safeFilename =
        file.filename || `file_${Date.now()}.${file.fileType}`;

      // Use data URL method (most reliable for mobile)
      const dataUrl = `data:${mimeType};base64,${file.base64Content}`;

      // Create and trigger download
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = dataUrl;
      link.download = safeFilename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // Fallback: open in new tab
      const mimeType = getMimeType(file.fileType);
      const dataUrl = `data:${mimeType};base64,${file.base64Content}`;
      window.open(dataUrl, "_blank");
    }
  };

  const getMimeType = (extension) => {
    const mimeTypes = {
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      txt: "text/plain",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      bmp: "image/bmp",
      webp: "image/webp",
    };
    return mimeTypes[extension] || "application/octet-stream";
  };

  const openPreview = (file) => {
    setPreviewFile(file);
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewFile(null);
  };

  return (
    <>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex justify-between items-center">
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dispatch(clearFilesError())}
            >
              <X className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="!p-4">
          <CardTitle className="text-base">
            <FormattedMessage
              id="FilesTab.Title"
              defaultMessage="Appointment Photos & Documents"
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="!p-4 !pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-600">
                <FormattedMessage
                  id="FilesTab.Loading"
                  defaultMessage="Loading files..."
                />
              </span>
            </div>
          ) : files.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              <FormattedMessage
                id="FilesTab.NoFiles"
                defaultMessage="No files uploaded yet"
              />
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {files.map((file) => {
                const previewSrc = getFilePreview(file);
                const isImage = previewSrc !== null;

                return (
                  <div
                    key={file.id}
                    className="border rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow relative group"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                      disabled={deleteLoading}
                      className="absolute top-1 left-1 z-10 w-6 !h-6 p-0 rounded-full bg-red-500 hover:bg-red-600 text-white opacity-90 hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </Button>

                    <div
                      className="aspect-video bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center relative cursor-pointer"
                      onClick={() => openPreview(file)}
                    >
                      {isImage ? (
                        <img
                          src={previewSrc}
                          alt={file.filename}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                      )}
                      <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Eye className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <div className="p-2">
                      <p
                        className="text-xs font-medium truncate mb-2"
                        title={file.filename}
                      >
                        {file.filename}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadFile(file);
                        }}
                        className="w-full text-xs px-2 py-1"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        <FormattedMessage
                          id="FilesTab.Download"
                          defaultMessage="Download"
                        />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="fixed bottom-6 right-6 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              disabled={uploadLoading}
              className="w-14 h-14 rounded-full bg-blue-500 hover:bg-blue-600 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            >
              {uploadLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-white" />
              ) : (
                <Upload className="h-6 w-6 text-white" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={handleCameraCapture}>
              <CameraIcon className="h-4 w-4 mr-2" />
              <FormattedMessage
                id="FilesTab.TakePhoto"
                defaultMessage="Take Photo"
              />
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleGalleryUpload}>
              <Image className="h-4 w-4 mr-2" />
              <FormattedMessage
                id="FilesTab.ChooseGallery"
                defaultMessage="Choose from Gallery"
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="w-[95vw] max-w-4xl h-[100vh] max-h-[100vh] p-2 sm:p-6">
          <DialogHeader className="pb-0 !space-y-0">
            <div className="flex items-center justify-between text-sm sm:text-base pt-4">
              <span
                className="text-sm sm:text-base truncate overflow-hidden whitespace-nowrap max-w-[50vw] sm:max-w-[50vw]"
                title={previewFile?.filename} // shows full name on hover
              >
                {previewFile?.filename}
              </span>
              <div className="flex gap-1 sm:gap-2 flex-shrink-0 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => previewFile && downloadFile(previewFile)}
                  className="text-xs sm:text-sm px-2 py-1"
                >
                  <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span className="hidden sm:inline">
                    <FormattedMessage
                      id="FilesTab.Download"
                      defaultMessage="Download"
                    />
                  </span>
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="flex justify-center flex-1 min-h-0 overflow-hidden mt-2">
            {previewFile && getFilePreview(previewFile) ? (
              <img
                src={getFilePreview(previewFile)}
                alt={previewFile.filename}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            ) : (
              <div className="flex flex-col items-center text-gray-500 p-4">
                <FileText className="h-12 w-12 sm:h-16 sm:w-16 mb-4" />
                <p className="text-sm sm:text-base">
                  <FormattedMessage
                    id="FilesTab.NoPreview"
                    defaultMessage="Preview not available for this file type"
                  />
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <input
        id="file-input"
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.txt"
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            handleFileUpload(Array.from(e.target.files));
            e.target.value = "";
          }
        }}
        disabled={uploadLoading}
      />
    </>
  );
}

export default FilesTab;
